import angleSmallDownSvg from "@icons/angle-small-down.svg?url";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "@/common/components/button/button";
import { InputField } from "@/common/components/form/input-field/input-field";
import { Spinner } from "@/common/components/spinner/spinner";
import { countriesQuery } from "@/routes/shared/api/countries.api";

import { useAssignUserCompanyMutation } from "../user.api";

type AssignCompanyFormProps = {
  userId: string;
};

type FormData = {
  country: string;
  companyNumber: string;
};

const AssignCompanyForm = ({ userId }: AssignCompanyFormProps) => {
  const countries = useSuspenseQuery(countriesQuery());
  const assignUserCompany = useAssignUserCompanyMutation();
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);

  const form = useForm<FormData>({
    defaultValues: {
      companyNumber: "",
      country: "",
    },
  });

  const onSubmit = (data: FormData) => {
    setError(null);
    assignUserCompany.mutate(
      {
        data: {
          companyNumber: data.companyNumber,
          country: data.country,
          role: "USER",
          state: "WAITING",
        },
        userId,
      },
      {
        onError: (error) => {
          setError(error.message || t`Něco se pokazilo`);
        },
        onSuccess: () => {
          form.reset();
          void queryClient.invalidateQueries();
        },
      },
    );
  };

  return (
    <details className="mt-4 border-t pt-4">
      <summary>
        <Trans>Přiřadit ke společnosti</Trans>
      </summary>
      <FormProvider {...form}>
        <form
          className="flex flex-col gap-y-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <label htmlFor="country">
            <Trans>Země</Trans>
            <select
              style={{ backgroundImage: `url(${angleSmallDownSvg})` }}
              {...form.register("country", { required: true })}
              className="mt-2 block w-full appearance-none rounded-lg border bg-white bg-[right_16px_center] bg-no-repeat p-4 pr-12 font-bold"
              id="country"
            >
              <option value="">
                <Trans>Vyberte zemi</Trans>
              </option>
              {countries.data.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.localName}
                </option>
              ))}
            </select>
          </label>

          <InputField<FormData>
            label={<Trans>IČO společnosti</Trans>}
            name="companyNumber"
            options={{ required: true }}
          />

          {error && <div className="text-red-500 text-sm">{error}</div>}

          <Button
            disabled={assignUserCompany.status === "pending"}
            type="submit"
            variant="primary"
          >
            {assignUserCompany.status === "pending" ? (
              <Spinner withHint={false} />
            ) : (
              <Trans>Přiřadit</Trans>
            )}
          </Button>
        </form>
      </FormProvider>
    </details>
  );
};

export { AssignCompanyForm };
